import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../../web/validation";
import {Entity} from "../entity";
import {getDatabaseCodeTransformer} from "../transfomers/transformers";
import {ServiceEventType, toServiceEventType} from "./service-event-type";
import {toTransportCategory, TransportCategory} from "../transport-category";

export const ServiceEventTicketPolicyCodec = t.intersection([
  t.type({
    id: t.string,
    companyId: t.string,
    regionId: t.string,
    name: t.string,
    eventType: t.string,
    category: t.string,
    freightTypeId: t.string,
    priority: t.number,
    doNotUse: t.boolean,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    description: t.string,
    serviceTypeId: t.string,
    triggerFacet: t.string,
    triggerFromStatus: t.string,
    triggerToStatus: t.string,
    minSeverity: t.number,
    defaultQuantityUom: t.string,
    defaultQuantity: t.number,
    createdById: t.string,
    lastModifiedById: t.string,

    companyName: t.string,
    serviceTypeName: t.string,
    freightTypeName: t.string,
    triggerFacetDescription: t.string,
    triggerFromStatusDescription: t.string,
    triggerToStatusDescription: t.string,
  }),
]);

export type ServiceEventTicketPolicy = t.TypeOf<typeof ServiceEventTicketPolicyCodec>;

@Exclude()
export class ServiceEventTicketPolicyEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  companyId!: string;

  @Expose()
  companyName?: string;

  @Expose()
  regionId!: string;

  @Expose()
  name!: string;

  @Expose()
  description?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toServiceEventType))
  eventType!: ServiceEventType;

  @Expose()
  serviceTypeId?: string;

  @Expose()
  serviceTypeName?: string;

  @Expose()
  triggerFacet?: string;

  @Expose()
  triggerFacetDescription?: string;

  @Expose()
  triggerFromStatus?: string;

  @Expose()
  triggerFromStatusDescription?: string;

  @Expose()
  triggerToStatus?: string;

  @Expose()
  triggerToStatusDescription?: string;

  @Expose()
  minSeverity?: number;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toTransportCategory))
  category!: TransportCategory

  @Expose()
  freightTypeId!: string;

  @Expose()
  freightTypeName?: string;

  @Expose()
  defaultQuantity?: number;

  @Expose()
  defaultQuantityUom?: string;

  @Expose()
  priority!: number;

  @Expose()
  doNotUse?: boolean;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  readonly entityType = 'SVCEVENTTICKETPOLICY';

  get displayName(): Readonly<string> {
    return this.name ?? this.id;
  }

  /**
   * Constructor for ServiceEventTicketPolicyEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: ServiceEventTicketPolicy): ServiceEventTicketPolicyEntity {
    return plainToInstance(ServiceEventTicketPolicyEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<ServiceEventTicketPolicy> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ServiceEventTicketPolicyCodec, object);
  }

  asTypeValue(): ServiceEventTicketPolicy {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(ServiceEventTicketPolicyCodec, object);
  }
}
